import type { BrowserWindow, Experiments } from '@wix/thunderbolt-symbols'
import { convertIdSelectorToClassSelector } from '@wix/thunderbolt-dom-utils'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

export type CssOverrides = { [prop: string]: string }
export type CssOverridesWithPriorities = { [prop: string]: { value: string; priority?: string } }

export const getRuntimeStyleOverridesManager = () => {
	const stylePropertiesToRestore = new WeakMap<NonNullable<BrowserWindow>, { [selector: string]: CssOverrides }>()

	const getWindowStylePropertiesToRestore = (window: NonNullable<BrowserWindow>) => {
		let windowStylePropertiesToRestore = stylePropertiesToRestore.get(window)
		if (!windowStylePropertiesToRestore) {
			windowStylePropertiesToRestore = {}
			stylePropertiesToRestore.set(window, windowStylePropertiesToRestore)
		}

		return windowStylePropertiesToRestore
	}

	const styleOverrides = {
		setItemCssOverrides: (
			cssOverridesWithPriorities: CssOverridesWithPriorities,
			selector: string,
			window: NonNullable<BrowserWindow>
		) => {
			if (!process.env.browser && process.env.NODE_ENV !== 'test') {
				return
			}
			const windowStylePropertiesToRestore = getWindowStylePropertiesToRestore(window)
			const nodes = window.document.querySelectorAll<HTMLElement>(selector)
			nodes.forEach((node) => {
				windowStylePropertiesToRestore[selector] = windowStylePropertiesToRestore[selector] || {}
				Object.entries(cssOverridesWithPriorities).forEach(([prop, override]) => {
					if (typeof windowStylePropertiesToRestore[selector][prop] === 'undefined') {
						// first override of this prop - save the original value to restore it later
						windowStylePropertiesToRestore[selector][prop] = node.style.getPropertyValue(prop)
					}

					node.style.setProperty(prop, override.value, override.priority)
				})
			})
		},
		clearItemCssOverrides: (selector: string, window: NonNullable<BrowserWindow>) => {
			const windowStylePropertiesToRestore = stylePropertiesToRestore.get(window)
			const itemOverridesOriginalValues = windowStylePropertiesToRestore?.[selector]
			if (!itemOverridesOriginalValues) {
				return
			}

			const nodes = window.document.querySelectorAll<HTMLElement>(selector)
			nodes.forEach((node) => {
				Object.entries(itemOverridesOriginalValues).forEach(([prop, originalValue]) => {
					node.style.setProperty(prop, originalValue)
				})

				if (node.getAttribute('style') === '') {
					node.removeAttribute('style')
				}
			})

			delete windowStylePropertiesToRestore![selector]
		},
		clearAllItemsCssOverrides: (window: NonNullable<BrowserWindow>) => {
			const windowStylePropertiesToRestore = stylePropertiesToRestore.get(window)
			if (!windowStylePropertiesToRestore) {
				return
			}

			Object.keys(windowStylePropertiesToRestore).forEach((selector) => {
				styleOverrides.clearItemCssOverrides(selector, window)
			})
		},
	}

	return styleOverrides
}

export const createRuntimeOverrideCompSelector = (experiments: Experiments) => {
	const useClassSelectors =
		isExperimentOpen(experiments, 'specs.thunderbolt.useClassSelectorsForLookup') &&
		isExperimentOpen(experiments, 'specs.thunderbolt.addIdAsClassName')
	return (compId: string) => (useClassSelectors ? convertIdSelectorToClassSelector(`#${compId}`) : `#${compId}`)
}
